import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Component
import CardImage from "components/molecules/sofia-magic-studio/card-image";
import ModalImagesSelector from "components/molecules/shared/modal-images-selector";
import ModalConfirm from "components/molecules/sofia-magic-studio/modal-confirm";
import Button from "components/v1/Button";

// Assets
import IInfo from "assets/i-info";
import ITidiedUp from "assets/i-tidied-up";
import IArrowShortail from "assets/i-arrow-shortail";
import ICheckRounded from "assets/i-check-rounded.js";
import ImgRapikanFotoSofia from "assets/image-rapikan-foto-sofia.png";
import ImgGunakanFotoSofia from "assets/image-gunakan-foto-sofia.png";
import ImgBatalFotoSofia from "assets/image-batalkan-foto-sofia.png";

// Apis
import {
  useGetSofiaListingImages,
  usePostSofiaListingImages,
  usePostSofiaListingImagesCancel,
  usePostSofiaListingImagesUse,
} from "api/sofia-magic-studio";

const TidiedUpPhoto = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const slug = searchParams.get("slug") ?? "";
  const sofiaProcessed = searchParams.get("sofia_processed") === "true";

  const [selectedImages, setSelectedImages] = useState([]);

  const { data: sofiaListingImages, refetch: refetchSofiaListingImages } =
    useGetSofiaListingImages({ slug });

  const {
    mutate: postSofiaListingImages,
    isLoading: isLoadingPostSofiaListingImages,
  } = usePostSofiaListingImages();

  const {
    mutate: postSofiaListingImagesCancel,
    isLoading: isPostingSofiaListingImagesCancel,
  } = usePostSofiaListingImagesCancel();

  const {
    mutate: postSofiaListingImagesUse,
    isLoading: isPostingSofiaListingImagesUse,
  } = usePostSofiaListingImagesUse();

  const dataImages = useMemo(
    () => sofiaListingImages?.data?.data || [],
    [sofiaListingImages],
  );

  const hasDraftImage = useMemo(
    () =>
      sofiaListingImages?.data?.data?.some((item) => item.status === "draft") ||
      false,
    [sofiaListingImages],
  );

  const [isOpenModal, setIsOpenModal] = useState(""); // "gallery" | "confirm-clean" | "confirm-cancel" | "confirm-use"
  const [initialPosition, setInitialPosition] = useState(0);

  const handleOpenImage = (idx) => {
    setInitialPosition(idx);
    setIsOpenModal("gallery");
  };

  const handleOpenModalConfirmClean = () => {
    setIsOpenModal("confirm-clean");
  };

  const handleCloseModal = () => {
    setIsOpenModal("");
    setInitialPosition(0);
  };

  const handleSelectImage = (item) => {
    const newSelectedImages = [...selectedImages];
    const isSelected = newSelectedImages.find((img) => img.id === item.id);

    if (isSelected) {
      newSelectedImages.splice(newSelectedImages.indexOf(isSelected), 1);
    } else {
      newSelectedImages.push(item);
    }
    setSelectedImages(newSelectedImages);
  };

  const handleBack = () => {
    if (hasDraftImage) {
      setIsOpenModal("confirm-cancel");
    } else {
      navigateBack();
    }
  };

  const navigateBack = () => {
    navigate(`/sofia-magic-studio?sofia_processed=${sofiaProcessed}`);
  };

  const _getImageFlagging = (item) => {
    const isSelected = selectedImages.find((img) => img.id === item.id);
    const hasProcessing = sofiaListingImages?.data?.data?.some(
      (item) => item.status === "processing",
    );

    const res = {
      isDraft: false,
      isSelect: false,
      isProcessing: false,
      isDisabled: hasProcessing ? true : false,
    };

    if (
      (isSelected && isLoadingPostSofiaListingImages) ||
      item?.status === "processing"
    ) {
      res.isProcessing = true;
    } else if (isLoadingPostSofiaListingImages) {
      res.isDisabled = true;
    } else if (item?.status === "draft" && !isSelected) {
      res.isDraft = true;
    }

    if (isSelected) {
      res.isSelect = true;
    }

    return res;
  };

  const handleProcessedImages = () => {
    const data = {
      property_image_ids: selectedImages.map((img) => img.id),
    };

    postSofiaListingImages(
      { slug, data },
      {
        onSuccess: () => {
          toast.success("Foto berhasil dirapikan");
          setSelectedImages([]);
          refetchSofiaListingImages();
          handleCloseModal();
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || "Gagal merapikan foto");
        },
        onSettled: () => {
          setIsOpenModal("");
        },
      },
    );
  };

  const handleProcessedImagesCancel = () => {
    postSofiaListingImagesCancel(
      { slug },
      {
        onSuccess: () => {
          toast.success("Foto berhasil dibatalkan");
          refetchSofiaListingImages();
          setSelectedImages([]);
          navigateBack();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message || "Gagal membatalkan foto",
          );
        },
      },
    );
  };

  const handleProcessedImagesUse = () => {
    postSofiaListingImagesUse(
      { slug },
      {
        onSuccess: () => {
          toast.success("Foto berhasil dirapikan");
          refetchSofiaListingImages();
          setSelectedImages([]);
          navigateBack();
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || "Gagal merapikan foto");
        },
      },
    );
  };

  useEffect(() => {
    console.log("isLoadingPostSofiaListingImages", isLoadingPostSofiaListingImages);
  }, [isLoadingPostSofiaListingImages]);

  const creditUsage = useMemo(() => {
    const draftLen =
      sofiaListingImages?.data?.data?.filter((item) => item.status === "draft")
        ?.length || 0;
    const imgLen = selectedImages?.length ?? 0;
    let res = 0;

    if (draftLen >= 1) {
      res = imgLen;
    } else if (imgLen <= 3) {
      res = 3;
    } else {
      res = imgLen;
    }
    return res;
  }, [selectedImages, sofiaListingImages]);

  const hasProcessingImages = useMemo(
    () =>
      sofiaListingImages?.data?.data?.some(
        (item) => item.status === "processing",
      ) || false,
    [sofiaListingImages],
  );

  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    if (hasProcessingImages) {
      pollingIntervalRef.current = setInterval(() => {
        refetchSofiaListingImages();
      }, 5000);
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [hasProcessingImages, refetchSofiaListingImages]);

  const disabledButton =
    hasProcessingImages ||
    isLoadingPostSofiaListingImages ||
    isPostingSofiaListingImagesCancel ||
    isPostingSofiaListingImagesUse;
  return (
    <>
      <div className="px-6 py-[14px] bg-white flex items-center justify-between">
        <Button
          type="secondary"
          size="s"
          icon={IArrowShortail}
          iconColor="#252526"
          text="Kembali"
          onClick={handleBack}
          disabled={disabledButton}
        />
      </div>
      <div className="overflow-x-auto">
        <div className="p-6 flex flex-col items-center justify-center w-[1035px] mx-auto">
          <p className="text-[33px] tracker-[-0.99px] leading-[39px] font-inter-bold text-[#252526] mb-2">
            Pilih foto yang ingin dirapikan.
          </p>
          <div className="px-2 py-0.5 flex items-center gap-2 rounded-[2px] bg-[#EBF0F7] mb-6">
            <IInfo color="#3268AE" />
            {hasDraftImage ? (
              <span className="text-[13px] tracker-[-0.4px] leading-[21.034px] font-inter text-[#3268AE]">
                Perapihan pertama selesai. Tinjau hasilnya atau rapikan foto
                lainnya.
              </span>
            ) : (
              <span className="text-[13px] tracker-[-0.4px] leading-[21.034px] font-inter text-[#3268AE]">
                Perapihan pertama: hingga 3 foto menggunakan 3 kredit.
              </span>
            )}
          </div>
          {!!dataImages?.length && (
            <div className="grid grid-cols-5 gap-2 min-h-[380px]">
              {dataImages.map((item, index) => (
                <CardImage
                  key={index}
                  data={item}
                  onOpenModalImages={() => handleOpenImage(index)}
                  onSelect={() => handleSelectImage(item)}
                  {..._getImageFlagging(item)}
                />
              ))}
            </div>
          )}
          <div className="flex items-center justify-end w-full gap-4">
            <p className="text-[11px] tracker-[0.11px] leading-[15px] font-inter text-[#686A68] py-1 px-2 rounded-[2px] bg-[#F5F5F5]">
              Penggunaan kredit:{" "}
              <span className="font-inter-bold text-[#040704]">
                {creditUsage}
              </span>
            </p>

            <div className="flex items-center gap-1">
              {!!selectedImages?.length && (
                <Button
                  type="primary"
                  size="md"
                  icon={ITidiedUp}
                  text="Rapikan"
                  className={`${hasDraftImage ? "!bg-[#fff] !text-[#252526]" : "!bg-[#1B67DA]"} !text-white my-4 mr-4 last:mr-0`}
                  onClick={handleOpenModalConfirmClean}
                  disabled={!selectedImages?.length}
                  iconColor={hasDraftImage ? "#252526" : "#FFFFFF"}
                />
              )}

              {hasDraftImage && (
                <Button
                  type="primary"
                  size="md"
                  icon={ICheckRounded}
                  iconColor="#FFFFFF"
                  text="Gunakan Hasil"
                  className="!bg-[#1B67DA] !text-white my-4 mr-4"
                  onClick={() => setIsOpenModal("confirm-use")}
                  disabled={disabledButton}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <ModalImagesSelector
        isOpen={isOpenModal === "gallery"}
        listImages={dataImages?.map((item) => item?.url) || []}
        onClose={handleCloseModal}
        initialPosition={initialPosition}
      />
      <ModalConfirm
        isOpen={isOpenModal === "confirm-clean"}
        onClose={handleCloseModal}
        onConfirm={handleProcessedImages}
        title="Rapikan foto yang dipilih?"
        description={`Aksi ini akan menggunakan ${creditUsage} kredit dari total kreditmu.`}
        rightBtnText="Rapikan Foto Terpilih"
        leftBtnText="Batal"
        image={ImgRapikanFotoSofia}
        disabled={disabledButton}
      />
      <ModalConfirm
        isOpen={isOpenModal === "confirm-use"}
        onClose={handleCloseModal}
        onConfirm={handleProcessedImagesUse}
        title="Gunakan hasil perapihan?"
        description="Foto yang telah dirapikan akan digunakan pada listing ini. Kamu masih bisa merapikan foto lain nanti."
        rightBtnText="Gunakan Hasil"
        leftBtnText="Batal"
        rightBtnIcon={ICheckRounded}
        image={ImgGunakanFotoSofia}
        disabled={disabledButton}
      />
      <ModalConfirm
        isOpen={isOpenModal === "confirm-cancel"}
        onClose={handleCloseModal}
        onConfirm={navigateBack}
        title="Batalkan hasil perubahan?"
        description="Jika dibatalkan, foto akan kembali seperti semula dan kredit tetap terpakai. Draft menyimpan hasil perapihan terakhirmu."
        rightBtnText="Jadikan draft"
        leftBtnText="Batalkan"
        leftBtnAction={handleProcessedImagesCancel}
        rightBtnIcon={ICheckRounded}
        image={ImgBatalFotoSofia}
        disabled={disabledButton}
      />
    </>
  );
};

export default TidiedUpPhoto;
